import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Company, LocationGroup, MaintenanceType, Segment } from '@core/interfaces';
import { clearEmptyProperties, mapCheckbox } from '@core/utils/filters-utils';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { DatePeriodValidation } from '@validators/date-period-validation';
import { DateValidation } from '@validators/date.validation';
import * as moment from 'moment';
import { debounceTime, tap } from 'rxjs/operators';
import english from '../../maintenances/i18n/en.json';
import spanish from '../../maintenances/i18n/es.json';
import portuguese from '../../maintenances/i18n/pt.json';
import { ReportFilterApi } from './services/report-filter-api.service';
import { ReportFilterState } from './services/report-filter-state.service';

@Component({
  selector: 'filters',
  templateUrl: './filters.component.html'
})
export class FiltersComponent implements OnInit {
  public filtersForm: FormGroup;

  public segments: Segment[] = [];
  public companies: Company[] = [];
  public locationsGroups: LocationGroup[] = [];
  public maintenanceTypes: MaintenanceType[] = [];

  constructor(
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly filterStateService: ReportFilterState,
    private readonly filtersQueries: ReportFilterApi,
    private readonly formBuilder: FormBuilder,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  public async ngOnInit(): Promise<void> {
    const data = await this.filtersQueries.getFiltersData();

    this.segments = data.segments;
    this.companies = data.companies;
    this.locationsGroups = data.locationsGroups;
    this.maintenanceTypes = data.maintenanceTypes;

    this.filtersForm = this.formBuilder.group({
      openedAtGte: [moment().subtract('1', 'month').startOf('days'), DateValidation.validate],
      openedAtLte: [moment().endOf('days'), DateValidation.validate],
      scheduledAtGte: [null, DateValidation.validate],
      scheduledAtLte: [null, DateValidation.validate],
      closedAtGte: [null, DateValidation.validate],
      closedAtLte: [null, DateValidation.validate],
      locationId: new FormControl(),
      locationsGroups: new FormArray(this.locationsGroups.map(() => new FormControl(false))),
      segments: new FormArray(this.segments.map(() => new FormControl(false))),
      maintenanceTypes: new FormArray(this.maintenanceTypes.map(() => new FormControl(false))),
      companies: new FormControl([])
    }, {
      validators: DatePeriodValidation.validate(['openedAt', 'closedAt', 'scheduledAt'])
    });

    this.filtersForm.valueChanges.pipe(debounceTime(500))
      .subscribe(() => {
        if (!this.filtersForm.valid) {
          return;
        }
        const openedAtGte = this.filtersForm.value.openedAtGte;
        const openedAtLte = this.filtersForm.value.openedAtLte;
        const scheduledAtGte = this.filtersForm.value.scheduledAtGte;
        const scheduledAtLte = this.filtersForm.value.scheduledAtLte;
        const closedAtGte = this.filtersForm.value.closedAtGte;
        const closedAtLte = this.filtersForm.value.closedAtLte;

        this.filterStateService.query = {
          openedAt_gte: openedAtGte && openedAtGte.startOf('day').format(),
          openedAt_lte: openedAtLte && openedAtLte.endOf('day').format(),
          scheduledAt_gte: scheduledAtGte && scheduledAtGte.startOf('day').format(),
          scheduledAt_lte: scheduledAtLte && scheduledAtLte.endOf('day').format(),
          closedAt_gte: closedAtGte && closedAtGte.startOf('day').format(),
          closedAt_lte: closedAtLte && closedAtLte.endOf('day').format(),
          typeId_in: mapCheckbox(this.filtersForm.value.maintenanceTypes, this.maintenanceTypes, 'id'),
          segmentId_in: mapCheckbox(this.filtersForm.value.segments, this.segments, 'id'),
          locationGroupId_in: mapCheckbox(this.filtersForm.value.locationsGroups, this.locationsGroups, 'id'),
          locationId_in: this.filtersForm.value.locationId,
          companyId_in: this.filtersForm.value.companies
        };

        if (this.filtersForm.value.openedAtGte) {
          this.filterStateService.query.openedAt_gte = this.filtersForm.value.openedAtGte.startOf('day').format();
        } else {
          this.filterStateService.query.openedAt_gte = moment().subtract('1', 'month').startOf('days').format();
          this.filtersForm.get('openedAtGte').setValue(moment().subtract('1', 'month').startOf('days'));
        }

        if (this.filtersForm.value.openedAtLte) {
          this.filterStateService.query.openedAt_lte = this.filtersForm.value.openedAtLte.startOf('day').format();
        } else {
          this.filterStateService.query.openedAt_lte = moment().endOf('days').format();
          this.filtersForm.get('openedAtLte').setValue(moment().endOf('days'));
        }
        this.filterStateService.query = clearEmptyProperties(this.filterStateService.query);
        this.filterStateService.onChangeFilter.next();
      });

    this.filterStateService.onClearFilter.pipe(tap(() => {
      this.filtersForm.reset();
      this.filtersForm.get('openedAtLte').setValue(moment().endOf('days'));
      this.filtersForm.get('openedAtGte').setValue(moment().subtract('1', 'month').startOf('days'));
    })).subscribe();

  }

  public getPropertyValue(value, array, property): any {
    const item = array.find((a) => a.id === value);
    return item[property];
  }
}
